/** Transform /volume/signals API bars for lightweight-charts. */

const OBV_LABEL_DISPLAY = {
  accumulation: 'Accumulation',
  distribution: 'Distribution',
  neutral: 'Neutral',
};

/** @param {'day'|'unix'} timeMode */
export function barChartTime(time, timeMode = 'day') {
  if (!time) return null;
  if (timeMode === 'unix') {
    return Math.floor(new Date(time).getTime() / 1000);
  }
  return String(time).slice(0, 10);
}

/** @param {'day'|'unix'} timeMode */
export function sortSignalBars(bars, timeMode = 'day') {
  return [...(bars || [])].sort((a, b) => {
    const ta = barChartTime(a.time, timeMode);
    const tb = barChartTime(b.time, timeMode);
    if (ta == null || tb == null) return 0;
    if (timeMode === 'unix') return ta - tb;
    return String(ta).localeCompare(String(tb));
  });
}

function firstObvInWindow(sorted) {
  const row = sorted.find((bar) => bar.obv != null);
  return row != null ? Number(row.obv) : null;
}

/** @param {'day'|'unix'} timeMode */
export function buildVolumeHistogramData(bars, timeMode = 'day') {
  const sorted = sortSignalBars(bars, timeMode);
  return sorted
    .filter((bar) => bar.volume != null)
    .map((bar, index) => {
      const prevClose = index > 0 ? sorted[index - 1].close : bar.close;
      const up = bar.close != null && prevClose != null && bar.close >= prevClose;
      const spike = bar.high_volume_2x === true;
      let color;
      if (spike) {
        color = up ? '#A17A17' : '#A14F17';
      } else {
        color = up ? 'rgba(31, 78, 140, 0.60)' : 'rgba(192, 87, 31, 0.60)';
      }
      return {
        time: barChartTime(bar.time, timeMode),
        value: Number(bar.volume),
        color,
      };
    });
}

/** OBV rebased to zero at the first bar in the window (matches its own scale). */
/** @param {'day'|'unix'} timeMode */
export function buildObvLineData(bars, timeMode = 'day') {
  const sorted = sortSignalBars(bars, timeMode).filter((bar) => bar.obv != null);
  const first = firstObvInWindow(sorted);
  return sorted.map((bar) => ({
    time: barChartTime(bar.time, timeMode),
    value:
      bar.obv_normalized != null
        ? Number(bar.obv_normalized)
        : first != null
          ? Number(bar.obv) - first
          : Number(bar.obv),
  }));
}

/** @param {'day'|'unix'} timeMode */
export function buildObvEmaLineData(bars, timeMode = 'day') {
  const sorted = sortSignalBars(bars, timeMode).filter((bar) => bar.obv_ema_20 != null);
  const first = firstObvInWindow(
    sortSignalBars(bars, timeMode).filter((bar) => bar.obv != null),
  );
  if (first == null) return [];
  return sorted.map((bar) => ({
    time: barChartTime(bar.time, timeMode),
    value: Number(bar.obv_ema_20) - first,
  }));
}

/** @param {'day'|'unix'} timeMode */
export function buildAdtvLineData(bars, timeMode = 'day') {
  return sortSignalBars(bars, timeMode)
    .filter((bar) => bar.adtv21 != null)
    .map((bar) => ({
      time: barChartTime(bar.time, timeMode),
      value: Number(bar.adtv21),
    }));
}

/** Markers for the main candlestick series. */
/** @param {'day'|'unix'} timeMode */
export function buildVolumeMarkers(bars, timeMode = 'day') {
  const markers = [];
  sortSignalBars(bars, timeMode).forEach((bar) => {
    const time = barChartTime(bar.time, timeMode);
    if (time == null) return;
    if (bar.selling_climax) {
      markers.push({
        time,
        position: 'belowBar',
        color: '#C0571F',
        shape: 'arrowUp',
        text: 'Climax',
      });
    }
    if (bar.confirmed_breakout) {
      markers.push({
        time,
        position: 'belowBar',
        color: '#1B9D4B',
        shape: 'arrowUp',
        text: 'OBV brk',
      });
    }
    if (bar.bearish_obv_divergence) {
      markers.push({
        time,
        position: 'aboveBar',
        color: '#A16917',
        shape: 'circle',
        text: 'OBV−',
      });
    }
    if (bar.bullish_obv_divergence) {
      markers.push({
        time,
        position: 'belowBar',
        color: '#1F4E8C',
        shape: 'circle',
        text: 'OBV+',
      });
    }
  });
  return markers;
}

export function trendBadgeLabel(latestTrend) {
  if (!latestTrend?.trend_direction) return null;
  const dir = latestTrend.trend_direction;
  if (dir === 'flat') return 'Volume trend: flat';
  const confirmed = latestTrend.confirmed === true;
  const unconfirmed = latestTrend.confirmed === false;
  if (confirmed) return `Volume confirms ${dir}trend`;
  if (unconfirmed) return `${dir}trend · volume not confirmed`;
  return `${dir}trend · volume n/a`;
}

export function obvBadgeLabel(latestObv) {
  if (!latestObv?.obv_label) return null;
  return OBV_LABEL_DISPLAY[latestObv.obv_label] || latestObv.obv_label;
}
